<template>
  <el-container class="app-container">
    <!-- 左侧导航菜单 -->
    <el-aside width="280px" class="category-container" :class="{ collapsed: categoryCollapsed }">
      <div class="category-header">
        <span class="title">产品分类</span>
        <div>
          <el-button
              type="primary"
              size="small"
              @click="categoryCollapsed = !categoryCollapsed">
            {{ categoryCollapsed ? '展开分类' : '收起分类' }}
          </el-button>
          <el-button
              type="primary"
              size="small"
              @click="addRootCategory">
            <el-icon><Plus /></el-icon>
            添加根分类
          </el-button>
          <el-button
              type="primary"
              size="small"
              @click="refreshData">
            <el-icon><Refresh /></el-icon>
            刷新
          </el-button>
        </div>
      </div>

      <el-tree
          v-if="rawCategories.length > 0"
          :data="processedCategories"
          node-key="categoryid"
          :props="treeProps"
          :expand-on-click-node="false"
          draggable
          highlight-current
          @node-drop="handleDrop"
          @node-click="handleNodeClick"
          class="category-tree"
      >
        <template #default="{ node, data }">
          <div class="tree-node-container">
            <div class="node-info">
              <span class="category-name">{{ data.categoryname }}</span>
              <span class="product-count" v-if="data.productCount">({{ data.productCount }})</span>
            </div>
            <div class="node-actions">
              <el-button
                  @click.stop="() => editCategory(data)"
                  circle
                  size="small"
                  title="编辑分类"
              ><el-icon><Edit /></el-icon></el-button>
              <el-button
                  @click.stop="() => remove(node, data)"
                  circle
                  size="small"
                  title="删除分类"
              ><el-icon><Delete style="color:darkred" /></el-icon></el-button>
              <el-button
                  @click.stop="() => showAddProductDrawer(data)"
                  circle
                  size="small"
                  title="添加产品"
              ><el-icon><Plus /></el-icon></el-button>
            </div>
          </div>
        </template>
      </el-tree>
      
      <el-empty v-else description="暂无分类" :image-size="60" />
    </el-aside>

    <!-- 主内容区 -->
    <el-main>
      <el-card class="box-card">
        <div class="table-header">
          <div class="table-title">
            产品列表
            <el-tag v-if="currentCategory" type="info">
              {{ currentCategory.categoryname }}
            </el-tag>
            <el-tag v-else type="info">全部产品</el-tag>
          </div>
          <div class="header-actions">
            <el-input
              v-model="searchKeyword"
              placeholder="搜索型号或名称"
              :prefix-icon="Search"
              clearable
              class="search-input"
              style="width: 250px; margin-right: 10px"
            />
            <el-button
              type="primary"
              size="small"
              @click="addProduct">
              <el-icon><Plus /></el-icon>
              添加产品
            </el-button>
          </div>
        </div>

        <el-table
            v-if="pagedProductList.length > 0"
            :data="pagedProductList"
            style="width: 100%"
            border
            v-loading="loading"
            @row-dblclick="showProductDetail"
        >
          <el-table-column label="图片" width="80" align="center">
            <template #default="{row}">
              <el-image
                v-if="row.photo"
                :src="resolveImageUrl(row.photo)"
                style="width: 50px; height: 50px"
                fit="cover"
                :preview-src-list="[resolveImageUrl(row.photo)]"
                preview-teleported
              />
              <el-image v-else style="width: 50px; height: 50px" fit="cover">
                <template #error>
                  <div class="image-placeholder">
                    <el-icon><Picture /></el-icon>
                  </div>
                </template>
              </el-image>
            </template>
          </el-table-column>
          <el-table-column prop="productcode" label="型号" width="100" sortable />
          <el-table-column prop="productname" label="产品名称" width="200" sortable />
          <el-table-column prop="description" label="描述" show-overflow-tooltip />
          <el-table-column prop="productwarranty" label="质保期" width="100" align="center">
            <template #default="{row}">
              {{ row.productwarranty || 0 }}个月
            </template>
          </el-table-column>
          <el-table-column prop="unitprice" label="建议售价" width="180">
            <template #default="{row}">
              <div>{{ formatPrice(row.unitprice) }}</div>
              <div v-if="row.unitprice && usdToCnyRate" class="price-cny">{{ formatPriceCNY(row.unitprice) }}</div>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120">
            <template #default="{row}">
              <el-button size="small" @click.stop="editProductItem(row)"><el-icon><Edit /></el-icon></el-button>
              <el-button size="small" @click.stop="confirmDeleteProduct(row)" style="color: #F56C6C"><el-icon><Delete /></el-icon></el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <el-empty v-else description="暂无产品" :image-size="100" />

        <!-- 分页控件 -->
        <el-pagination
            :current-page="pagination.currentPage"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
        />
      </el-card>

      <!-- 分类编辑对话框 -->
      <el-dialog
          v-model="categoryDialogVisible"
          :title="categoryDialogTitle"
          width="500px"
      >
        <el-form :model="currentCategory" label-width="80px">
          <el-form-item label="分类名称" required>
            <el-input v-model="currentCategory.categoryname"></el-input>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="categoryDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveCategory">保存</el-button>
        </template>
      </el-dialog>

      <!-- 添加/编辑产品抽屉 -->
      <el-drawer
          v-model="addProductDrawerVisible"
          :title="productDrawerTitle"
          size="50%"
          :before-close="handleClose"
      >
          <el-form :model="currentProduct"
                   label-position="top"
                   ref="productForm"
                   :rules="rules">
              <el-row :gutter="20">
                  <el-col :span="12">
                      <el-form-item label="所属分类" prop="categoryid">
                          <el-select v-model="currentProduct.categoryid"
                                     placeholder="请选择分类"
                                     style="width: 100%">
                              <el-option label="未分组" :value="null"></el-option>
                              <el-option v-for="item in flatCategories"
                                         :key="item.categoryid"
                                         :label="item.categoryname"
                                         :value="item.categoryid">
                                  <span v-for="(space, index) in item.level" :key="index" style="display: inline-block; width: 15px"></span>
                                  {{ item.categoryname }}
                              </el-option>
                          </el-select>
                      </el-form-item>
                  </el-col>
                  <el-col :span="12">
                      <el-form-item label="产品型号" prop="productcode" required>
                          <el-input v-model="currentProduct.productcode" />
                      </el-form-item>
                  </el-col>
              </el-row>

              <el-row :gutter="20">
                  <el-col :span="12">
                      <el-form-item label="产品名称" prop="productname" required>
                          <el-input v-model="currentProduct.productname" />
                      </el-form-item>
                  </el-col>
                  <el-col :span="6">
                      <el-form-item label="建议售价(USD)">
                          <el-input-number v-model="currentProduct.unitprice"
                                           :precision="2"
                                           :step="1"
                                           :min="0"
                                           style="width: 100%" />
                      </el-form-item>
                  </el-col>
                  <el-col :span="6">
                      <el-form-item label="质保期(月)" prop="productwarranty">
                          <el-input-number v-model="currentProduct.productwarranty"
                                           :step="6"
                                           :min="12"
                                           style="width: 100%" />
                      </el-form-item>
                  </el-col>
              </el-row>

              <el-form-item label="产品图片">
                  <ProductImageUpload
                    v-model="currentProduct.photo"
                    :product-id="currentProduct.productid || 0"
                    @uploaded="onProductPhotoUploaded"
                  />
              </el-form-item>
              <el-form-item label="产品参数">
                  <el-button type="primary"
                             plain
                             @click="addParameter"
                             size="small">
                      <el-icon><Plus /></el-icon>
                      添加参数
                  </el-button>

                  <el-table :data="currentProduct.parameters"
                            border
                            empty-text="暂无参数"
                            class="parameter-table">
                      <el-table-column prop="name"
                                       label="参数名"
                                       width="180">
                          <template #default="{row, $index}">
                              <el-input v-model="row.name"
                                        placeholder="请输入参数名"
                                        @change="validateParameter($index)" />
                          </template>
                      </el-table-column>

                      <el-table-column prop="value" label="参数值">
                          <template #default="{row, $index}">
                              <el-input v-model="row.value"
                                        placeholder="请输入参数值"
                                        @change="validateParameter($index)" />
                          </template>
                      </el-table-column>

                      <el-table-column label="操作"
                                       width="80"
                                       align="center">
                          <template #default="{$index}">
                              <el-button type="danger"
                                         @click="removeParameter($index)"
                                         circle><el-icon><Delete /></el-icon></el-button>
                          </template>
                      </el-table-column>
                  </el-table>
              </el-form-item>
              <el-form-item label="海关代码" property="hscode">
                  <el-input v-model="currentProduct.hscode" type="text"/>
              </el-form-item>
              <el-divider content-position="left">包装信息</el-divider>
              <el-row :gutter="20">
                <el-col :span="8">
                  <el-form-item label="长度(cm)">
                    <el-input-number v-model="currentProduct.pkglength" :precision="2" :step="0.1" :min="0" style="width: 100%" />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="宽度(cm)">
                    <el-input-number v-model="currentProduct.pkgwidth" :precision="2" :step="0.1" :min="0" style="width: 100%" />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="高度(cm)">
                    <el-input-number v-model="currentProduct.pkgheight" :precision="2" :step="0.1" :min="0" style="width: 100%" />
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="每箱数量 (Pcs/Ctn)">
                    <el-input-number v-model="currentProduct.packingqty" :min="0" :precision="0" :step="1" style="width: 100%" />
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="净重(kg)">
                    <el-input-number v-model="currentProduct.netweight" :precision="2" :step="0.1" :min="0" style="width: 100%" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="毛重(kg)">
                    <el-input-number v-model="currentProduct.grossweight" :precision="2" :step="0.1" :min="0" style="width: 100%" />
                  </el-form-item>
                </el-col>
              </el-row>
              <el-form-item label="产品描述" prop="description">
                  <el-input v-model="currentProduct.description"
                            type="textarea"
                            :rows="3" />
              </el-form-item>

          </el-form>

        <div class="drawer-footer">
          <el-button @click="addProductDrawerVisible = false">取消</el-button>
          <el-button type="primary" @click="saveProduct">保存</el-button>
        </div>
      </el-drawer>

      <!-- 产品详情抽屉 -->
      <el-drawer
          v-model="productDetailDrawerVisible"
          title="产品详情"
          size="80%"
          :before-close="handleDetailClose"
      >
        <el-tabs>
          <el-tab-pane label="基本信息">
            <!-- 图片展示区 -->
            <div v-if="currentProduct.photo" class="product-image-container" style="margin-bottom: 20px; text-align: center">
              <el-image
                :src="resolveImageUrl(currentProduct.photo)"
                style="max-width: 300px; max-height: 300px"
                fit="contain"
                :preview-src-list="[resolveImageUrl(currentProduct.photo)]"
                :initial-index="0"
                preview-teleported
              />
            </div>
            
            <el-descriptions :column="2" border>
              <el-descriptions-item label="产品名称">{{ currentProduct.productname }}</el-descriptions-item>
              <el-descriptions-item label="产品型号">{{ currentProduct.productcode }}</el-descriptions-item>
              <el-descriptions-item label="所属分类">
                {{ getCategoryName(currentProduct.categoryid) || '未分组' }}
              </el-descriptions-item>
              <el-descriptions-item label="建议售价">
                <div>{{ formatPrice(currentProduct.unitprice) }}</div>
                <div v-if="currentProduct.unitprice && usdToCnyRate" class="price-cny">{{ formatPriceCNY(currentProduct.unitprice) }}</div>
              </el-descriptions-item>
              <el-descriptions-item label="质保期">
                {{ currentProduct.productwarranty || 0 }}个月
              </el-descriptions-item>
              <el-descriptions-item label="海关代码">
                {{ currentProduct.hscode || '未设置' }}
              </el-descriptions-item>
              <el-descriptions-item label="包装尺寸" v-if="currentProduct.pkglength || currentProduct.pkgwidth || currentProduct.pkgheight">
                {{ currentProduct.pkglength }}×{{ currentProduct.pkgwidth }}×{{ currentProduct.pkgheight }} cm
              </el-descriptions-item>
              <el-descriptions-item label="净重" v-if="currentProduct.netweight">
                {{ currentProduct.netweight }} kg
              </el-descriptions-item>
              <el-descriptions-item label="毛重" v-if="currentProduct.grossweight">
                {{ currentProduct.grossweight }} kg
              </el-descriptions-item>
              <el-descriptions-item label="每箱数量" v-if="currentProduct.packingqty">
                {{ currentProduct.packingqty }} Pcs/Ctn
              </el-descriptions-item>
              <el-descriptions-item label="产品描述" :span="2">
                {{ currentProduct.description || '暂无描述' }}
              </el-descriptions-item>
            </el-descriptions>
            
            <!-- 参数表 -->
            <div v-if="getProductParas(currentProduct.productparas).length > 0" style="margin-top: 20px">
              <h3>产品参数</h3>
              <el-table :data="getProductParas(currentProduct.productparas)" border style="width: 100%">
                <el-table-column prop="name" label="参数名" width="180" />
                <el-table-column prop="value" label="参数值" />
              </el-table>
            </div>
          </el-tab-pane>
        </el-tabs>

        <div class="drawer-footer">
          <el-button @click="editProduct">编辑</el-button>
          <el-button type="danger" @click="confirmDelete">删除</el-button>
        </div>
      </el-drawer>
    </el-main>
  </el-container>
</template>

<script>
import { Plus, Delete, Edit, Refresh, Picture, Search } from '@element-plus/icons-vue'
import { post } from '@/api/index'
import exchangeRate from '@/utils/exchangeRate'
import ProductImageUpload from '@/components/ProductImageUpload.vue'

export default {
  components: {
    Plus, Delete, Edit, Refresh, Picture, Search, ProductImageUpload
  },
  data() {
    return {
      rawCategories: [],
      flatCategories: [],
      currentCategory: null,
      categoryDialogVisible: false,
      categoryDialogTitle: '添加分类',

      usdToCnyRate: null,

      treeProps: {
        label: 'categoryname',
        children: 'children'
      },

      productList: [],
      filteredProductList: [],
      loading: false,

      currentProduct: this.getDefaultProduct(),
      addProductDrawerVisible: false,
      productDetailDrawerVisible: false,
      productDrawerTitle: '添加产品',
      activeTab: 'basic',

      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },

      searchKeyword: '',
  // reserved
      categoryCollapsed: false,
      rules: {
        productname: [{required: true, message: '请输入产品名称', trigger: 'blur'}],
        productcode: [{required: true, message: '请输入产品型号', trigger: 'blur'}],
        categoryid: [{required: true, message: '请选择产品分类', trigger: 'change'}]
      }
    }
  },
  watch: {
    searchKeyword() {
      this.updateFilteredProducts()
    }
  },
  computed: {
    processedCategories() {
      const map = {}
      const roots = []

      this.rawCategories.forEach(item => {
        map[item.categoryid] = { ...item, children: [], productCount: 0 }
      })

      this.productList.forEach(product => {
        if (product.categoryid && map[product.categoryid]) {
          map[product.categoryid].productCount += 1
        }
      })

      this.rawCategories.forEach(item => {
        const node = map[item.categoryid]
        if (item.parentid === 0 || !item.parentid) {
          roots.push(node)
        } else {
          const parent = map[item.parentid]
          if (parent) {
            parent.children.push(node)
          }
        }
      })

      Object.values(map).forEach(node => {
        if (node.children.length > 0) {
          node.children.sort((a, b) => a.categoryname.localeCompare(b.categoryname))
        }
      })

      return roots.sort((a, b) => a.categoryname.localeCompare(b.categoryname))
    },
    pagedProductList() {
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize
      const end = start + this.pagination.pageSize
      return this.filteredProductList.slice(start, end)
    }
  },
  created() {
    this.loadCategories()
    this.loadProducts()
    this.loadExchangeRate()
  },
  methods: {
    resolveImageUrl(url) {
      if (!url) return ''
      if (url.startsWith('http') || url.startsWith('/apiserve')) return url
      return '/apiserve/crmapi/' + url
    },
    stripImageUrlPrefix(url) {
      if (!url) return ''
      if (url.startsWith('/apiserve/crmapi/')) return url.replace('/apiserve/crmapi/', '')
      if (url.startsWith('/crmapi/')) return url.replace('/crmapi/', '')
      return url
    },
    showProductDetail(row) {
      this.currentProduct = JSON.parse(JSON.stringify(row))
      this.productDetailDrawerVisible = true
    },

    addParameter() {
      this.currentProduct.parameters.push({ name: '', value: '' })
    },
    removeParameter(index) {
      this.currentProduct.parameters.splice(index, 1)
    },
    validateParameter(index) {
      const param = this.currentProduct.parameters[index]
      if (!param.name && !param.value) {
        this.removeParameter(index)
      } else if (!param.name || !param.value) {
        this.$message.warning('请完整填写参数名和参数值')
      }
    },
    getDefaultProduct() {
      return {
        id: null,
        categoryid: this.currentCategory?.categoryid || null,
        productname: '',
        productcode: '',
        description: '',
        unitprice: 0,
        images: [],
        parameters: [],
        documents: [],
        productwarranty: 12,
        hscode: '9031809090',
        pkglength: null,
        pkgwidth: null,
        pkgheight: null,
        netweight: null,
        grossweight: null,
        packingqty: null,
      }
    },
    refreshData() {
      this.loadCategories()
      this.loadProducts()
      this.$message.success('数据已刷新')
    },
    loadCategories() {
      this.loading = true
      post('products.asp', { action: 'categories' })
          .then(response => {
            this.rawCategories = (response.categories || response.data?.categories) || []
            this.buildFlatCategories()
          })
          .catch(error => {
            console.error('加载分类失败:', error)
            this.$message.error('加载分类失败')
          })
          .finally(() => {
            this.loading = false
          })
    },
    buildFlatCategories() {
      const flatList = []
      const build = (nodes, level = 0) => {
        nodes.forEach(node => {
          flatList.push({ ...node, level })
          if (node.children && node.children.length) {
            build(node.children, level + 1)
          }
        })
      }
      build(this.processedCategories)
      this.flatCategories = flatList
    },
    getCategoryName(categoryid) {
      if (!categoryid) return null
      const category = this.flatCategories.find(item => item.categoryid === categoryid)
      return category ? category.categoryname : null
    },
    
    getProductParas(productparas) {
      try {
        if (!productparas) return []
        const paras = typeof productparas === 'string' ? JSON.parse(productparas) : productparas
        return Array.isArray(paras) ? paras : []
      } catch (e) {
        console.error('解析产品参数失败:', e)
        return []
      }
    },
    loadProducts() {
      this.loading = true
      post('products.asp', { action: 'products' })
          .then(response => {
            this.productList = (response.products || response.data?.products) || []
            this.updateFilteredProducts()
          })
          .catch(error => {
            console.error('加载产品失败:', error)
            this.$message.error('加载产品失败')
          })
          .finally(() => {
            this.loading = false
          })
    },
    updateFilteredProducts() {
      this.pagination.currentPage = 1
      
      let result = this.productList
      
      // 按分类过滤
      if (this.currentCategory) {
        result = result.filter(
            product => product.categoryid === this.currentCategory.categoryid
        )
      }
      
      // 按搜索关键字过滤
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase()
        result = result.filter(
            product => 
              (product.productcode && product.productcode.toLowerCase().includes(keyword)) ||
              (product.productname && product.productname.toLowerCase().includes(keyword))
        )
      }
      
      this.filteredProductList = result
      this.pagination.total = this.filteredProductList.length
    },
    addRootCategory() {
      this.currentCategory = {
        categoryid: null,
        categoryname: '',
        parentid: 0
      }
      this.categoryDialogTitle = '添加根分类'
      this.categoryDialogVisible = true
    },
    editCategory(data) {
      this.currentCategory = {
        categoryid: data.categoryid,
        categoryname: data.categoryname,
        parentid: data.parentid || 0
      }
      this.categoryDialogTitle = '编辑分类'
      this.categoryDialogVisible = true
    },
    saveCategory() {
      if (!this.currentCategory.categoryname) {
        this.$message.warning('请输入分类名称')
        return
      }

      const isAdd = !this.currentCategory.categoryid
      const action = isAdd ? 'addcata' : 'updatecata'
      post('products.asp', {
        action: action,
        ...this.currentCategory,
      })
          .then(() => {
            this.$message.success('保存成功')
            this.categoryDialogVisible = false
            this.loadCategories()
          })
          .catch(error => {
            console.error('保存分类失败:', error)
            this.$message.error('保存失败')
          })
    },
    remove(node, data) {
      this.$confirm('确定删除该分类吗? 其下所有子分类和产品将变为未分类状态', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
          .then(() => {
            post('products.asp', { action: 'deletecata', categoryid: data.categoryid })
                .then(() => {
                  this.$message.success('删除成功')
                  this.loadCategories()
                  this.loadProducts()
                })
                .catch(error => {
                  console.error('删除分类失败:', error)
                  this.$message.error('删除失败')
                })
          })
          .catch(() => {})
    },
    handleDrop(draggingNode, dropNode, dropType) {
      const movedNodeId = draggingNode.data.categoryid
      const moveNodeName = draggingNode.data.categoryname
      const targetNodeId = dropNode.data.categoryid
      const parentId = dropType === 'inner' ? targetNodeId : dropNode.parent.data?.categoryid || 0
      post('products.asp', {
        action: 'updatecata',
        categoryid: movedNodeId,
        categoryname: moveNodeName,
        parentid: parentId
      })
          .then(() => {
            this.$message.success('分类顺序已保存')
            this.loadCategories()
          })
          .catch(error => {
            console.error('保存分类顺序失败:', error)
            this.$message.error('保存分类顺序失败')
          })
    },
    handleNodeClick(data) {
      this.currentCategory = data
      this.updateFilteredProducts()
    },

    addProduct() {
      this.resetProductForm()
      this.productDrawerTitle = '添加产品'
      this.addProductDrawerVisible = true
      this.activeTab = 'basic'
    },

    showAddProductDrawer(data) {
      this.resetProductForm()
      this.productDrawerTitle = '添加产品'
      this.currentProduct.categoryid = data.categoryid
      this.addProductDrawerVisible = true
      this.activeTab = 'basic'
    },

    resetProductForm() {
      this.currentProduct = this.getDefaultProduct()
      if (this.currentCategory) {
        this.currentProduct.categoryid = this.currentCategory.categoryid
      }
    },
    saveProduct() {
      this.$refs.productForm.validate(valid => {
        if (valid) {
          const isAdd = !this.currentProduct.productid
          const action = isAdd ? 'addproduct' : 'updateproduct'
          const successMsg = isAdd ? '添加成功' : '更新成功'
          const paras = JSON.stringify(this.currentProduct.parameters)
          post('products.asp', {
            action: action,
            ProductID: this.currentProduct.productid,
            ProductCode: this.currentProduct.productcode,
            ProductName: this.currentProduct.productname,
            CategoryID: this.currentProduct.categoryid,
            UnitPrice: this.currentProduct.unitprice,
            Photo: this.stripImageUrlPrefix(this.currentProduct.photo),
            Description: this.currentProduct.description,
            ProductParas: paras,
            ProductWarranty: this.currentProduct.productwarranty,
            HSCode: this.currentProduct.hscode,
            PkgLength: this.currentProduct.pkglength || '',
            PkgWidth: this.currentProduct.pkgwidth || '',
            PkgHeight: this.currentProduct.pkgheight || '',
            NetWeight: this.currentProduct.netweight || '',
            GrossWeight: this.currentProduct.grossweight || '',
            PackingQty: this.currentProduct.packingqty || '',
          })
              .then(() => {
                this.$message.success(successMsg)
                this.addProductDrawerVisible = false
                this.loadProducts()
              })
              .catch(error => {
                console.error('保存产品失败:', error)
                this.$message.error('保存失败')
              })
        }
      })
    },

    editProductItem(row) {
      row.parameters = JSON.parse(row.productparas || "[]")
      this.currentProduct = JSON.parse(JSON.stringify(row))
      this.productDrawerTitle = '编辑产品'
      this.addProductDrawerVisible = true
      this.activeTab = 'basic'
    },

    editProduct() {
      this.productDetailDrawerVisible = false
      this.editProductItem(this.currentProduct)
    },

    confirmDelete() {
      this.deleteProduct(this.currentProduct.productid)
    },

    confirmDeleteProduct(row) {
      this.deleteProduct(row.productid)
    },

    deleteProduct(productId) {
      this.$confirm('确定删除该产品吗?', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
          .then(() => {
            post('products.asp', {
              productid: productId,
              action: 'product_delete'
            })
                .then(() => {
                  this.$message.success('删除产品成功')
                  this.productDetailDrawerVisible = false
                  this.loadProducts()
                })
                .catch(error => {
                  console.error('删除产品失败:', error)
                  this.$message.error('删除失败')
                })
          })
          .catch(() => {})
    },

    formatPrice(price) {
      if (!price && price !== 0) return '$0'
      const num = parseFloat(price)
      if (isNaN(num)) return '$0'
      return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 2 })
    },

    formatPriceCNY(price) {
      if (!price || !this.usdToCnyRate) return ''
      const num = parseFloat(price) * this.usdToCnyRate
      if (isNaN(num)) return ''
      return '≈ ¥' + num.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },

    async loadExchangeRate() {
      try {
        this.usdToCnyRate = await exchangeRate.getUSDtoCNY()
      } catch (e) {
        console.error('获取汇率失败', e)
      }
    },

    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.currentPage = 1
    },

    handleCurrentChange(val) {
      this.pagination.currentPage = val
    },

    onProductPhotoUploaded(url) {
      this.currentProduct.photo = url
    },

    handleClose(done) {
      this.$confirm('确认关闭？数据将不会被保存', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
          .then(() => done())
          .catch(() => {})
    },

    handleDetailClose(done) {
      done()
    }
  }
}
</script>

<style scoped>
.category-container {
  background-color: #f5f7fa;
  padding: 15px;
  border-right: 1px solid #ebeef5;
  height: 100%;
  overflow: auto;
  transition: all 0.3s ease;
}

.category-container.collapsed {
  display: none;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  flex-wrap: wrap;
  gap: 10px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
}

.table-title {
  font-size: 16px;
  font-weight: bold;
}

.tree-node-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 5px 0;
}

.node-info {
  display: flex;
  align-items: center;
}

.node-actions {
  display: flex;
  opacity: 0;
  transition: opacity 0.2s;
}

.tree-node-container:hover .node-actions {
  opacity: 1;
}

.category-name {
  margin-right: 5px;
}

.product-count {
  font-size: 12px;
  color: #909399;
}

.image-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.el-pagination {
  margin-top: 15px;
}

.el-main {
  min-width: 0;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .el-aside {
    width: 100% !important;
    height: auto;
    max-height: 300px;
    overflow-y: auto;
  }
  
  .category-container {
    padding: 10px;
  }
  
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-actions {
    width: 100%;
    flex-direction: column;
  }
  
  .search-input {
    width: 100% !important;
  }
}

.price-cny {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}
</style>
